/**
 * Aufmaß → Rechnung (pure, testbar)
 *
 * Wandelt LV-Positionen mit erfasstem Aufmaß in Rechnungspositionen um.
 * Als Menge wird – je nach Option – die noch offene oder die gesamte
 * Ist-Menge aus dem Aufmaß übernommen (nicht die LV-Soll-Menge).
 *
 * Positionen ohne Aufmaß-Menge (bzw. mit Menge 0) werden übersprungen,
 * ebenso Zwischenüberschriften (isHeader).
 */

import type { QuoteItem } from './schema';
import { istMenge, offeneMenge, roundMenge } from './aufmass';
import { isExcludedFromMainSum } from './quote-totals';

export interface InvoicePositionFromAufmass {
  title: string;
  description?: string;
  /** GAEB-Langtext der LV-Position — landet in invoice_items.long_description
   * (Kurz-/Langtext-Umschalter der Rechnung, ZUGFeRD/XRechnung-Description). */
  longDescription?: string;
  positionNumber?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  /** true = LV-Position hatte KEINEN erfassten Preis (preisFehlt). unitPrice ist dann 0.
   * Aufrufer MÜSSEN das sichtbar machen (Warnung) — eine Rechnung über 0,00 € für eine
   * unbepreist importierte Position wäre still falsch. */
  priceMissing?: boolean;
  vatRate: number;
  gewerk?: string;
  budgetTaskId?: string;
  type: 'service';
}

export interface BuildInvoicePositionsOptions {
  /** true = nur die noch nicht abgerechnete (offene) Ist-Menge übernehmen. */
  nurOffene?: boolean;
}

/** Kurztext einer Position: Name, sonst erste nicht-leere Zeile der Beschreibung. */
function kurztext(item: QuoteItem): string {
  const name = item.name?.trim();
  if (name) return name;
  const firstLine = (item.beschreibung || '')
    .split('\n')
    .map((line) => line.trim())
    .find((line) => line.length > 0);
  return firstLine || 'Position';
}

/**
 * Baut Rechnungspositionen aus dem Aufmaß der übergebenen LV-Positionen.
 * Die Menge ist die (offene bzw. gesamte) Ist-Menge, auf 3 Nachkommastellen gerundet.
 */
export function buildInvoicePositionsFromAufmass(
  items: QuoteItem[],
  opts: BuildInvoicePositionsOptions = {},
): InvoicePositionFromAufmass[] {
  const positions: InvoicePositionFromAufmass[] = [];

  for (const item of items) {
    if (item.isHeader || isExcludedFromMainSum(item)) continue;

    const menge = opts.nurOffene ? offeneMenge(item.aufmass) : istMenge(item.aufmass);
    const quantity = roundMenge(menge);
    if (!(quantity > 0)) continue;

    positions.push({
      title: kurztext(item),
      description: item.beschreibung?.trim() ? item.beschreibung : undefined,
      longDescription: item.longText?.trim() ? item.longText : undefined,
      positionNumber: item.positionNumber,
      quantity,
      unit: item.einheit || 'Stk',
      unitPrice: item.preis,
      priceMissing: item.preisFehlt === true || undefined,
      vatRate: item.umsatzsteuer,
      gewerk: item.gewerk,
      budgetTaskId: item.budgetTaskId,
      type: 'service',
    });
  }

  return positions;
}
